import { useState } from "react";
import styled from "styled-components";
import { motion } from "motion/react";

import Button from "Utilities/Button";
import useWizard from "./useWizard";

const stagger = {
  animate: { transition: { staggerChildren: 0.15 } },
  exit: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 150, damping: 15 } },
  exit: { opacity: 0, y: -80 },
};

const items = [
  {
    icon: "fa-clipboard-list",
    title: "We'll set everything up for you.",
    desc: "Family Registration, Signups, Emergency Contacts, Groups, Attendance Tracking - we've got it covered.",
  },
  {
    icon: "fa-money-check-dollar",
    title: "Flexible pricing to match your needs.",
    desc: "Flocknote RE easily handles early bird, catechist, scholarship, and other pricing options.",
  },
  {
    icon: "fa-envelope-open-text",
    title: "Everything else you need.",
    desc: "We'll even add draft emails & texts for you to customize inviting families to sign up.",
  },
];

export default function WizardOverview() {
  const { setShowOverview, beginSession, getSessions } = useWizard();
  const hasSessions = getSessions().length > 0;
  const [isExiting, setIsExiting] = useState(false);

  const handleBack = () => {
    setIsExiting(true);
  };

  return (
    <Wrapper
      as={motion.div}
      variants={stagger}
      initial="initial"
      animate={isExiting ? "exit" : "animate"}
      onAnimationComplete={() => {
        if (isExiting) setShowOverview(false);
      }}
    >
      <Top>
        <Title as={motion.div} variants={fadeUp} transition={{ duration: 0.35 }} className="merri">
          Here's the game plan
        </Title>
        <Text as={motion.div} variants={fadeUp} transition={{ duration: 0.35 }} className="raleway">
          After a few quick steps, we'll tailor a Family Registration and Signup flow for your families to sign up for your Religious Education.
        </Text>
        <ItemList as={motion.div} variants={fadeUp} transition={{ duration: 0.35 }}>
          {items.map((item) => (
            <Item key={item.icon}>
              <IconCircle>
                <i className={`fas ${item.icon}`} />
              </IconCircle>
              <ItemText>
                <ItemTitle className="raleway">{item.title}</ItemTitle>
                <ItemDesc>{item.desc}</ItemDesc>
              </ItemText>
            </Item>
          ))}
        </ItemList>
      </Top>
      <Buttons as={motion.div} variants={fadeUp} transition={{ duration: 0.35 }}>
        {hasSessions && (
          <BackButton onClick={handleBack}>
            <span><i className="fas fa-circle-chevron-left" /> Back</span>
          </BackButton>
        )}
        <Disclaimer>
          We'll save everything as you go. Come back anytime and pick up where you left off.
        </Disclaimer>
        <Button onClick={beginSession}>
          Let's go! <i className="fas fa-arrow-right" />
        </Button>
      </Buttons>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  padding: 80px;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Top = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const Title = styled.div`
  font-size: 48px;
`;

const Text = styled.div`
  font-size: 22px;
  color: #6b7280;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 24px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #016099;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
  overflow: hidden;
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
`;

const ItemDesc = styled.div`
  font-size: 16px;
  color: #6b7280;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
`;

const Disclaimer = styled.div`
  font-size: 16px;
  color: #6b7280;
  max-width: 380px;
  text-align: left;
  line-height: 1.25;
  margin-right: auto;
`;

const BackButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 15px;
  color: #6b7280;
  padding: 8px 10px 8px 8px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #016099;
  }

  &:active {
    background: #016099;
    color: #fff;

    > span {
      transform: scale(0.85);
    }
  }

  > span {
    display: flex;
    align-items: center;
    gap: 4px;
    transition: transform 0.2s;
  }

  i {
    font-size: 1em;
    margin-right: 2px;
  }
`;
